/**
 * ActionButton - Stateful action button
 *
 * Core component: No internal dependencies.
 * Blue when active (ready to act), disabled-looking when inactive.
 *
 * Use for: submit buttons, confirm buttons, action triggers
 */

import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ActionButtonProps {
  /** Button content (text or icon) */
  children: React.ReactNode;
  /** Click handler */
  onClick: () => void;
  /** Whether the button is in active/ready state */
  isActive: boolean;
  /** Loading state (shows spinner) */
  isLoading?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Shape variant */
  shape?: 'pill' | 'circle';
  /** Additional className */
  className?: string;
}

export function ActionButton({
  children,
  onClick,
  isActive,
  isLoading = false,
  disabled = false,
  size = 'md',
  shape = 'pill',
  className,
}: ActionButtonProps) {
  const sizeStyles = {
    sm: shape === 'circle' ? 'h-9 w-9' : 'h-9 px-4 text-sm',
    md: shape === 'circle' ? 'h-12 w-12' : 'h-12 px-6 text-base',
    lg: shape === 'circle' ? 'h-14 w-14' : 'h-14 px-8 text-lg',
  };

  const isDisabled = disabled || isLoading;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isDisabled}
      className={cn(
        'flex items-center justify-center font-medium',
        'transition-all duration-200',
        'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
        'focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-1',
        shape === 'circle' ? 'rounded-full' : 'rounded-full',
        sizeStyles[size],
        isActive
          ? 'bg-sky-500 text-white hover:bg-sky-600 active:bg-sky-700'
          : 'bg-slate-100 text-slate-400',
        isDisabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : children}
    </button>
  );
}
